import React, { useMemo, useState } from "react";

interface TimePickerProps {
  initialTime?: string; // "HH:MM" format
  onConfirm: (time: string) => void;
  onCancel: () => void;
}

type Mode = "hour" | "minute";

function polarPosition(index: number, total: number, radius: number, center: number) {
  const angle = (index / total) * 360 - 90;
  const rad = (angle * Math.PI) / 180;
  const x = center + radius * Math.cos(rad);
  const y = center + radius * Math.sin(rad);
  return { x, y, angle };
}

export default function TimePicker({
  initialTime = "00:00",
  onConfirm,
  onCancel,
}: TimePickerProps) {
  const [hour24, setHour24] = useState(() => Math.max(0, Math.min(23, parseInt(initialTime.split(":")[0]) || 0)));
  const [minute, setMinute] = useState(() => Math.max(0, Math.min(59, parseInt(initialTime.split(":")[1]) || 0)));
  const [mode, setMode] = useState<Mode>("hour");

  // 12시간 표시용 계산 (UI 전용)
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const isPM = hour24 >= 12;

  const hourOuter = useMemo(() => Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i), []);
  const minuteMarks = useMemo(() => Array.from({ length: 12 }, (_, i) => i * 5), []);

  const selectedTimeText = `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

  const handleDialSelect = (val: number) => {
    if (mode === "hour") {
      // val은 12시간 형식 (1-12)
      const newHour24 = val === 12 ? (isPM ? 12 : 0) : (isPM ? val + 12 : val);
      setHour24(newHour24);
      setMode("minute");
    } else {
      setMinute(val);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedTimeText);
  };

  const toggleAMPM = (newIsPM: boolean) => {
    // AM/PM 토글 시 시간을 변환
    if (newIsPM && !isPM) {
      // AM → PM
      const newHour24 = hour12 === 12 ? 12 : hour12 + 12;
      setHour24(newHour24);
    } else if (!newIsPM && isPM) {
      // PM → AM
      const newHour24 = hour12 === 12 ? 0 : hour12;
      setHour24(newHour24);
    }
  };

  const center = 130;
  const outerRadius = 96;

  const pointerAngle = mode === "hour"
    ? ((hour12 % 12) / 12) * 360
    : (minute / 60) * 360;
  const pointerLength = outerRadius;

  const renderHourMarks = () => (
    <>
      {hourOuter.map((h, idx) => {
        const isSelected = (h === 12 && hour24 === 0) || (h === hour12);
        return (
          <button
            key={`hour-${h}`}
            onClick={() => handleDialSelect(h)}
            style={{
              position: "absolute",
              left: center + outerRadius * Math.cos((idx / 12) * 360 * Math.PI / 180 - Math.PI / 2) - 16,
              top: center + outerRadius * Math.sin((idx / 12) * 360 * Math.PI / 180 - Math.PI / 2) - 16,
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid #d0d7de",
              background: isSelected ? "#1b3fa0" : "#fff",
              color: isSelected ? "#fff" : "#222",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: isSelected ? "0 2px 8px rgba(27,63,160,0.35)" : "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            {h}
          </button>
        );
      })}
    </>
  );

  const renderMinuteMarks = () => (
    <>
      {minuteMarks.map((m, idx) => {
        const isSelected = m === minute - (minute % 5) && minute % 5 === 0;
        return (
          <button
            key={`minute-${m}`}
            onClick={() => handleDialSelect(m)}
            style={{
              position: "absolute",
              left: center + outerRadius * Math.cos((idx / 12) * 360 * Math.PI / 180 - Math.PI / 2) - 16,
              top: center + outerRadius * Math.sin((idx / 12) * 360 * Math.PI / 180 - Math.PI / 2) - 16,
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid #d0d7de",
              background: isSelected ? "#1b3fa0" : "#fff",
              color: isSelected ? "#fff" : "#222",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: isSelected ? "0 2px 8px rgba(27,63,160,0.35)" : "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            {String(m).padStart(2, "0")}
          </button>
        );
      })}
    </>
  );

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0, 0, 0, 0.45)",
      zIndex: 1000,
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 16,
        padding: "20px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
        width: "min(480px, 95vw)",
        boxSizing: "border-box",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#222" }}>시간 선택</div>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              onClick={() => toggleAMPM(false)}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: !isPM ? 700 : 600,
                border: !isPM ? "2px solid #1b3fa0" : "1px solid #d0d7de",
                background: !isPM ? "#e9edff" : "#fff",
                color: !isPM ? "#1b3fa0" : "#666",
                cursor: "pointer",
              }}
            >
              오전
            </button>
            <button
              onClick={() => toggleAMPM(true)}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: isPM ? 700 : 600,
                border: isPM ? "2px solid #1b3fa0" : "1px solid #d0d7de",
                background: isPM ? "#e9edff" : "#fff",
                color: isPM ? "#1b3fa0" : "#666",
                cursor: "pointer",
              }}
            >
              오후
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {/* Analog clock */}
          <div style={{ position: "relative", width: 260, height: 260, margin: "0 auto" }}>
            <div style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              width: 240,
              height: 240,
              borderRadius: "50%",
              background: "#f6f8ff",
              border: "1px solid #d0d7de",
              boxShadow: "inset 0 0 0 8px #fff",
            }} />

            {/* pointer */}
            <div style={{
              position: "absolute",
              left: center,
              top: center,
              width: 2,
              height: pointerLength,
              background: "#1b3fa0",
              transformOrigin: "bottom center",
              transform: `translate(-1px, -${pointerLength}px) rotate(${pointerAngle}deg)`,
              borderRadius: 2,
            }} />

            <div style={{
              position: "absolute",
              left: center - 6,
              top: center - 6,
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#1b3fa0",
              boxShadow: "0 0 0 4px #fff",
            }} />

            {mode === "hour" ? renderHourMarks() : renderMinuteMarks()}
          </div>

          {/* Controls */}
          <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              {/* AM/PM Dropdown */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>구분</label>
                <select
                  value={isPM ? "PM" : "AM"}
                  onChange={(e) => toggleAMPM(e.target.value === "PM")}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #d0d7de",
                    fontSize: 14,
                    fontWeight: 600,
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    minWidth: 80,
                  }}
                >
                  <option value="AM">오전</option>
                  <option value="PM">오후</option>
                </select>
              </div>

              {/* Hour Input */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>시간</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={hour12}
                  onClick={() => setMode("hour")}
                  onChange={(e) => {
                    const val = Math.max(1, Math.min(12, parseInt(e.target.value) || 1));
                    const newHour24 = isPM && val !== 12 ? val + 12 : isPM && val === 12 ? 12 : !isPM && val === 12 ? 0 : val;
                    setHour24(newHour24);
                  }}
                  style={{
                    width: 70,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #d0d7de",
                    fontSize: 14,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                />
              </div>

              <div style={{ fontSize: 20, fontWeight: 700, color: "#999" }}>:</div>

              {/* Minute Input */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>분</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={String(minute).padStart(2, "0")}
                  onClick={() => setMode("minute")}
                  onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                  style={{
                    width: 70,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #d0d7de",
                    fontSize: 14,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                />
              </div>
            </div>

            <div style={{ fontSize: 12, color: "#666" }}>
              좌측의 아날로그 다이얼에서 시/분을 선택하거나 위의 입력 필드로 직접 입력할 수 있습니다.
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={onCancel}
                style={{
                  padding: "10px 18px",
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 8,
                  border: "1px solid #d0d7de",
                  background: "#f5f5f5",
                  color: "#333",
                  cursor: "pointer",
                }}
              >
                취소
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: "10px 18px",
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 8,
                  border: "none",
                  background: "#1b3fa0",
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(27,63,160,0.3)",
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
